import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Progress } from '../ui/progress';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  Bot, 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  Zap, 
  Brain,
  Target,
  ShieldCheck,
  Activity,
  Settings,
  Play,
  Pause,
  BarChart3
} from 'lucide-react';
import { getAITradingService } from '../../services/AITradingService';
import { TradingSignal, PredictionResult, ArbitrageOpportunity } from '../../types/ai-trading';

interface TradingBotConfig {
  enabled: boolean;
  riskTolerance: 'conservative' | 'moderate' | 'aggressive';
  maxTradeAmount: number;
  autoExecute: boolean;
  strategies: string[];
  stopLoss: number;
  takeProfit: number;
}

export function AITradingBot() {
  const [isActive, setIsActive] = useState(false);
  const [signals, setSignals] = useState<TradingSignal[]>([]);
  const [predictions, setPredictions] = useState<PredictionResult[]>([]);
  const [arbitrageOps, setArbitrageOps] = useState<ArbitrageOpportunity[]>([]);
  const [botConfig, setBotConfig] = useState<TradingBotConfig>({
    enabled: false,
    riskTolerance: 'moderate',
    maxTradeAmount: 1000,
    autoExecute: false,
    strategies: ['technical-analysis', 'sentiment-analysis', 'arbitrage'],
    stopLoss: 5,
    takeProfit: 10
  });
  const [performance, setPerformance] = useState({
    totalTrades: 127,
    winRate: 68.5,
    profitLoss: 2847.32,
    sharpeRatio: 1.85
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isActive) {
      generateSignals();
      const interval = setInterval(generateSignals, 30000); // Update every 30 seconds
      return () => clearInterval(interval);
    }
  }, [isActive]);

  // Initialize with some default data
  useEffect(() => {
    if (signals.length === 0) {
      generateSignals();
    }
  }, []);

  const generateSignals = async () => {
    try {
      setLoading(true);
      
      // Generate trading signals
      const newSignals: TradingSignal[] = [
        {
          id: `signal-${Date.now()}-1`,
          type: 'buy',
          token: 'ETH',
          strength: 85,
          price: 2845.67,
          targetPrice: 3120.00,
          stopLoss: 2650.00,
          confidence: 0.82,
          timeframe: '4h',
          strategy: 'Technical Breakout',
          reasoning: [
            'RSI showing oversold conditions',
            'Breaking above resistance at $2800',
            'High volume confirmation',
            'Positive sentiment analysis'
          ],
          timestamp: new Date()
        },
        {
          id: `signal-${Date.now()}-2`,
          type: 'sell',
          token: 'BTC',
          strength: 72,
          price: 67845.23,
          targetPrice: 65200.00,
          stopLoss: 69500.00,
          confidence: 0.75,
          timeframe: '1d',
          strategy: 'Mean Reversion',
          reasoning: [
            'Overbought RSI levels',
            'Bearish divergence on MACD',
            'Strong resistance at $68k',
            'Decreasing volume'
          ],
          timestamp: new Date()
        },
        {
          id: `signal-${Date.now()}-3`,
          type: 'buy',
          token: 'LINK',
          strength: 91,
          price: 14.82,
          targetPrice: 18.50,
          stopLoss: 13.20,
          confidence: 0.89,
          timeframe: '2h',
          strategy: 'AI Sentiment',
          reasoning: [
            'Extremely bullish social sentiment',
            'Partnership announcement upcoming',
            'Technical breakout pattern',
            'Institutional accumulation detected'
          ],
          timestamp: new Date()
        }
      ];

      setSignals(newSignals);

      // Get price predictions
      const predictionTokens = ['ETH', 'BTC', 'LINK', 'UNI'];
      console.log('AI Trading Bot: Fetching predictions for tokens:', predictionTokens);
      
      const newPredictions = await Promise.all(
        predictionTokens.map(async (token) => {
          try {
            const prediction = await getAITradingService().getPricePrediction(token.toLowerCase(), '24h');
            console.log(`AI Trading Bot: Got prediction for ${token}:`, prediction);
            return prediction;
          } catch (error) {
            console.error(`AI Trading Bot: Error getting prediction for ${token}:`, error);
            // Return fallback prediction
            return {
              token: token.toLowerCase(),
              currentPrice: 1000,
              predictedPrice: 1050,
              timeframe: '24h',
              confidence: 0.5,
              direction: 'bullish' as const,
              keyFactors: ['Analysis pending'],
              riskLevel: 'medium' as const,
              timestamp: new Date()
            };
          }
        })
      );
      setPredictions(newPredictions);

      // Get arbitrage opportunities
      const opportunities = await getAITradingService().getArbitrageOpportunities();
      setArbitrageOps(opportunities.slice(0, 5));

    } catch (error) {
      console.error('Error generating signals:', error);
    } finally {
      setLoading(false);
    }
  };

  const executeSignal = async (signal: TradingSignal) => {
    if (!botConfig.autoExecute) {
      // Manual execution - would integrate with swap interface
      console.log('Manual execution required for signal:', signal);
      return;
    }

    try {
      // Auto-execution logic would go here
      console.log('Auto-executing signal:', signal);
      // Update performance metrics
      setPerformance(prev => ({
        ...prev,
        totalTrades: prev.totalTrades + 1
      }));
    } catch (error) {
      console.error('Error executing signal:', error);
    }
  };

  const getSignalIcon = (type: string) => {
    switch (type) {
      case 'buy': return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'sell': return <TrendingDown className="h-4 w-4 text-red-500" />;
      default: return <Activity className="h-4 w-4 text-blue-500" />;
    }
  };

  const getStrengthColor = (strength: number) => {
    if (strength >= 80) return 'text-green-500';
    if (strength >= 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900">
            <Bot className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h1>AI Trading Bot</h1>
            <p className="text-muted-foreground">
              Automated trading with advanced AI algorithms
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <span className="text-sm">Bot Status:</span>
            <Switch
              checked={isActive}
              onCheckedChange={setIsActive}
            />
            <Badge variant={isActive ? "default" : "secondary"}>
              {isActive ? "Active" : "Inactive"}
            </Badge>
          </div>
          
          <Button
            onClick={generateSignals}
            disabled={loading}
            variant="outline"
          >
            {loading ? (
              <span className="animate-spin">⟳</span>
            ) : (
              <Brain className="h-4 w-4 mr-2" />
            )}
            Analyze Market
          </Button>
        </div>
      </div>

      {/* Performance Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Trades</p>
                <p className="text-2xl font-bold">{performance.totalTrades}</p>
              </div>
              <Activity className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Win Rate</p>
                <p className="text-2xl font-bold text-green-500">{performance.winRate}%</p>
              </div>
              <Target className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">P&L</p>
                <p className="text-2xl font-bold text-green-500">
                  ${performance.profitLoss.toLocaleString()}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Sharpe Ratio</p>
                <p className="text-2xl font-bold">{performance.sharpeRatio}</p>
              </div>
              <BarChart3 className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="signals" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="signals">Trading Signals</TabsTrigger>
          <TabsTrigger value="predictions">Price Predictions</TabsTrigger>
          <TabsTrigger value="arbitrage">Arbitrage</TabsTrigger>
          <TabsTrigger value="config">Configuration</TabsTrigger>
        </TabsList>

        <TabsContent value="signals" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Zap className="h-5 w-5" />
                <span>Active Trading Signals</span>
                {loading && <span className="animate-spin ml-2">⟳</span>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {signals.map((signal) => (
                  <div
                    key={signal.id}
                    className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        {getSignalIcon(signal.type)}
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="font-semibold">{signal.token}</span>
                            <Badge 
                              variant={signal.type === 'buy' ? 'default' : 'destructive'}
                              className="uppercase"
                            >
                              {signal.type}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{signal.strategy}</p>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className={`text-lg font-bold ${getStrengthColor(signal.strength)}`}>
                          {signal.strength}%
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Confidence: {(signal.confidence * 100).toFixed(0)}%
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                      <div>
                        <p className="text-sm text-muted-foreground">Current Price</p>
                        <p className="font-semibold">${signal.price.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Target</p>
                        <p className="font-semibold text-green-500">
                          ${signal.targetPrice.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Stop Loss</p>
                        <p className="font-semibold text-red-500">
                          ${signal.stopLoss.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Timeframe</p>
                        <p className="font-semibold">{signal.timeframe}</p>
                      </div>
                    </div>

                    <div className="mb-3">
                      <p className="text-sm text-muted-foreground mb-1">Reasoning:</p>
                      <ul className="text-sm space-y-1">
                        {signal.reasoning.map((reason, index) => (
                          <li key={index} className="flex items-center space-x-2">
                            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                            <span>{reason}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="text-sm text-muted-foreground">
                        Generated: {signal.timestamp.toLocaleTimeString()}
                      </div>
                      
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => executeSignal(signal)}
                        >
                          {botConfig.autoExecute ? (
                            <Play className="h-4 w-4 mr-1" />
                          ) : (
                            <Settings className="h-4 w-4 mr-1" />
                          )}
                          {botConfig.autoExecute ? 'Execute' : 'Manual'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}

                {signals.length === 0 && !loading && (
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      No trading signals available. Market analysis in progress...
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="predictions" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {predictions.map((prediction) => (
              <Card key={prediction.token}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{prediction.token} Price Prediction</span>
                    <Badge 
                      variant={
                        prediction.direction === 'bullish' ? 'default' :
                        prediction.direction === 'bearish' ? 'destructive' : 'secondary'
                      }
                    >
                      {prediction.direction}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Current Price</p>
                        <p className="text-lg font-semibold">
                          ${prediction.currentPrice.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Predicted Price</p>
                        <p className={`text-lg font-semibold ${
                          prediction.predictedPrice > prediction.currentPrice 
                            ? 'text-green-500' : 'text-red-500'
                        }`}>
                          ${prediction.predictedPrice.toLocaleString()}
                        </p>
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm">Confidence</span>
                        <span className="text-sm font-semibold">
                          {(prediction.confidence * 100).toFixed(0)}%
                        </span>
                      </div>
                      <Progress value={prediction.confidence * 100} />
                    </div>

                    <div>
                      <p className="text-sm text-muted-foreground mb-2">Key Factors:</p>
                      <ul className="text-sm space-y-1">
                        {prediction.keyFactors.map((factor, index) => (
                          <li key={index} className="flex items-center space-x-2">
                            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                            <span>{factor}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="flex items-center justify-between">
                      <Badge variant={
                        prediction.riskLevel === 'low' ? 'default' :
                        prediction.riskLevel === 'medium' ? 'secondary' : 'destructive'
                      }>
                        {prediction.riskLevel} Risk
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {prediction.timeframe}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="arbitrage" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Cross-Chain Arbitrage Opportunities</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {arbitrageOps.map((op) => (
                  <div
                    key={op.id}
                    className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-semibold">{op.tokenA}</span>
                          <span className="text-muted-foreground">•</span>
                          <span className="text-sm">{op.chainA} → {op.chainB}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Spread: {op.spreadPercentage.toFixed(2)}%
                        </p>
                      </div>
                      
                      <div className="text-right">
                        <div className="text-lg font-bold text-green-500">
                          +${op.estimatedProfit.toFixed(0)}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Est. Profit
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 mb-3">
                      <div>
                        <p className="text-sm text-muted-foreground">Gas Cost</p>
                        <p className="font-semibold">${op.gasEstimate}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Execution Time</p>
                        <p className="font-semibold">{op.executionTime}s</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Risk Score</p>
                        <p className={`font-semibold ${
                          op.riskScore < 30 ? 'text-green-500' :
                          op.riskScore < 60 ? 'text-yellow-500' : 'text-red-500'
                        }`}>
                          {op.riskScore}/100
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="text-sm text-muted-foreground">
                        Price: ${op.priceA.toFixed(4)} → ${op.priceB.toFixed(4)}
                      </div>
                      
                      <Button size="sm" variant="outline">
                        <Zap className="h-4 w-4 mr-1" />
                        Execute
                      </Button>
                    </div>
                  </div>
                ))}

                {arbitrageOps.length === 0 && (
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      No profitable arbitrage opportunities found at the moment.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="config" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Bot Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4>Auto-Execute Trades</h4>
                  <p className="text-sm text-muted-foreground">
                    Automatically execute high-confidence signals
                  </p>
                </div>
                <Switch
                  checked={botConfig.autoExecute}
                  onCheckedChange={(checked) =>
                    setBotConfig(prev => ({ ...prev, autoExecute: checked }))
                  }
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Risk Tolerance</label>
                <div className="flex space-x-2">
                  {['conservative', 'moderate', 'aggressive'].map((risk) => (
                    <Button
                      key={risk}
                      variant={botConfig.riskTolerance === risk ? 'default' : 'outline'}
                      size="sm"
                      onClick={() =>
                        setBotConfig(prev => ({ 
                          ...prev, 
                          riskTolerance: risk as any 
                        }))
                      }
                    >
                      {risk.charAt(0).toUpperCase() + risk.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Max Trade Amount: ${botConfig.maxTradeAmount}
                </label>
                <input
                  type="range"
                  min="100"
                  max="10000"
                  step="100"
                  value={botConfig.maxTradeAmount}
                  onChange={(e) =>
                    setBotConfig(prev => ({ 
                      ...prev, 
                      maxTradeAmount: parseInt(e.target.value) 
                    }))
                  }
                  className="w-full"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Stop Loss ({botConfig.stopLoss}%)
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="20"
                    step="0.5"
                    value={botConfig.stopLoss}
                    onChange={(e) =>
                      setBotConfig(prev => ({ 
                        ...prev, 
                        stopLoss: parseFloat(e.target.value) 
                      }))
                    }
                    className="w-full"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Take Profit ({botConfig.takeProfit}%)
                  </label>
                  <input
                    type="range"
                    min="5"
                    max="50"
                    step="1"
                    value={botConfig.takeProfit}
                    onChange={(e) =>
                      setBotConfig(prev => ({ 
                        ...prev, 
                        takeProfit: parseFloat(e.target.value) 
                      }))
                    }
                    className="w-full"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}